import React, { useState, useEffect } from 'react';
import { useSession, useSupabaseClient } from '@supabase/auth-helpers-react';
import { Star, Check, Loader } from 'lucide-react';

interface FeedbackWidgetProps {
  appName: string;
  showWidget?: boolean;
}

export function FeedbackWidget({ appName, showWidget = false }: FeedbackWidgetProps) {
  const [heading, setHeading] = useState('Help us improve');
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState('');
  const [contactPermission, setContactPermission] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const session = useSession();
  const supabase = useSupabaseClient();

  useEffect(() => {
    async function fetchWidgetConfig() {
      try {
        const { data, error } = await supabase
          .from('feedback_widget_config')
          .select('heading, enabled')
          .single();

        if (error) throw error;
        if (data && data.enabled) {
          setHeading(data.heading);
        }
      } catch (err) {
        console.error('Error fetching widget config:', err);
      }
    }

    fetchWidgetConfig();
  }, [supabase]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!session?.user?.id) {
      setError('Please log in to submit feedback');
      return;
    }

    if (rating === 0) {
      setError('Please select a rating');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const { error: insertError } = await supabase
        .from('user_feedback')
        .insert({
          user_id: session.user.id,
          app_name: appName,
          rating,
          comment: comment.trim() || null,
          contact_permission: contactPermission
        });

      if (insertError) throw insertError;

      setSubmitted(true);
      setRating(0);
      setComment('');
      setContactPermission(false);
    } catch (err) {
      console.error('Error submitting feedback:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  if (!session || !showWidget) {
    return null;
  }

  if (submitted) {
    return (
      <div className="w-full my-6">
        <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 shadow-sm">
          <div className="flex items-center justify-center space-x-2 text-primary-600">
            <Check className="w-4 h-4 sm:w-5 sm:h-5" />
            <p className="text-sm sm:text-base font-medium">Thank you for your feedback!</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full my-6">
      <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 shadow-sm">
        <h3 className="text-sm sm:text-base font-medium text-gray-900 mb-3">{heading}</h3>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
              How would you rate this tool?
            </label>
            <div className="flex items-center space-x-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoveredRating(star)}
                  onMouseLeave={() => setHoveredRating(0)}
                  className="focus:outline-none transition-transform hover:scale-110 p-0.5"
                >
                  <Star
                    className={`w-5 h-5 sm:w-6 sm:h-6 ${
                      star <= (hoveredRating || rating)
                        ? 'fill-yellow-400 text-yellow-400'
                        : 'text-gray-300'
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>

          <div>
            <label htmlFor="comment" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
              Tell us more (optional)
            </label>
            <textarea
              id="comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="What did you like? What could be improved?"
              className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none text-xs sm:text-sm"
              rows={2}
            />
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="contact-permission"
                checked={contactPermission}
                onChange={(e) => setContactPermission(e.target.checked)}
                className="h-3.5 w-3.5 text-primary-600 focus:ring-primary-500 border-gray-300 rounded flex-shrink-0"
              />
              <label htmlFor="contact-permission" className="ml-1.5 text-xs text-gray-600">
                Contact me for details
              </label>
            </div>

            <button
              type="submit"
              disabled={submitting || rating === 0}
              className="flex items-center space-x-1.5 px-3 sm:px-4 py-1.5 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-xs sm:text-sm w-full sm:w-auto justify-center"
            >
              {submitting ? (
                <>
                  <Loader className="w-3.5 h-3.5 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Check className="w-3.5 h-3.5" />
                  <span>Submit</span>
                </>
              )}
            </button>
          </div>

          {error && (
            <div className="bg-gray-50 text-gray-700 p-2 rounded-2xl text-xs">
              {error}
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
